(() => {
  'use strict';
  if (customElements.get('logo-cup-mockup')) return;
  const ENDPOINT = 'https://images.jaxengraving.com/functions/v1/website-logo';
  const MAX_FILE = 2 * 1024 * 1024;
  const BRRWARE_HANDLE = 'custom-engraved-can-cooler-company-logo';
  const DEFAULT_CUP = { handle: BRRWARE_HANDLE, color: 'Black' };
  const validRect = value => value && ['x', 'y', 'width', 'height'].every(key => Number.isFinite(value[key])) && value.x >= 0 && value.y >= 0 && value.width > 0 && value.height > 0 && value.x + value.width <= 100 && value.y + value.height <= 100;
  const backPreview = variant => {
    if (!variant?.backEnabled || !variant.backImage || variant.backPreviewOnly || !validRect(variant.backArea)) return null;
    return { image: variant.backImage, area: variant.backArea, crop: validRect(variant.backImageCrop) ? variant.backImageCrop : { x: 0, y: 0, width: 100, height: 100 } };
  };
  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
  const imageFrom = source => new Promise((resolve, reject) => {
    const image = new Image();
    const timer = setTimeout(() => { image.src = ''; reject(new Error('photo_error')); }, 12000);
    image.crossOrigin = 'anonymous';
    image.onload = () => { clearTimeout(timer); resolve(image); };
    image.onerror = () => { clearTimeout(timer); reject(new Error('photo_error')); };
    image.src = source;
  });
  const dataUrlFrom = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    const finish = (done, value) => { clearTimeout(timer); reader.onload = reader.onerror = reader.onabort = null; done(value); };
    const timer = setTimeout(() => { finish(reject, new Error('file_error')); if (reader.readyState === 1) reader.abort(); }, 12000);
    reader.onload = () => finish(resolve, reader.result);
    reader.onerror = reader.onabort = () => finish(reject, new Error('file_invalid'));
    try { reader.readAsDataURL(file); } catch (error) { finish(reject, error); }
  });
  function prepareWithin(work, signal) {
    return new Promise((resolve, reject) => {
      let timer;
      const finish = (done, value) => { clearTimeout(timer); signal?.removeEventListener('abort', cancel); done(value); };
      const cancel = () => finish(reject, new Error('file_error'));
      if (signal?.aborted) { cancel(); return; }
      signal?.addEventListener('abort', cancel, { once: true });
      timer = setTimeout(cancel, 30000);
      Promise.resolve().then(work).then(value => finish(resolve, value), error => finish(reject, error));
    });
  }
  function websiteUrl(value) {
    const raw = value.trim();
    if (!raw || raw.length > 2048 || /\s/.test(raw)) throw new Error('invalid_url');
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port || !url.hostname.includes('.')) throw new Error('invalid_url');
    url.protocol = 'https:';
    url.hash = ''; url.search = '';
    return url.href;
  }
  function dataFile(value, mime, name) {
    if (typeof value !== 'string' || value.length > 3 * MAX_FILE || !['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(mime)) throw new Error('file_invalid');
    const prefix = `data:${mime};base64,`;
    if (!value.startsWith(prefix) || !/^[A-Za-z0-9+/]+={0,2}$/.test(value.slice(prefix.length))) throw new Error('file_invalid');
    const binary = atob(value.slice(prefix.length));
    if (!binary.length || binary.length > MAX_FILE) throw new Error('file_invalid');
    return new File([Uint8Array.from(binary, character => character.charCodeAt(0))], name, { type: mime });
  }
  async function prepareLogo(file, allowUnprepared = false) {
    if (!file?.size || file.size > MAX_FILE) throw new Error('file_invalid');
    // Some browsers provide no MIME for a valid local logo. The extension only
    // selects a decoder; the signature or sanitized SVG still verifies it.
    let mime = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
    if (['', 'application/octet-stream', 'binary/octet-stream'].includes(mime)) mime = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', svg: 'image/svg+xml' }[(file.name || '').split('.').pop().toLowerCase()];
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(mime)) throw new Error('file_invalid');
    if (file.type !== mime) file = new File([file], file.name, { type: mime, lastModified: file.lastModified });
    let source, original;
    if (file.type === 'image/svg+xml') {
      // The existing importer parses an allowlist and rejects scripts, foreign
      // content and linked resources before making any browser image request.
      const preview = await globalThis.EngravingFileImport.render(file);
      source = preview.source;
      original = await dataUrlFrom(file);
    } else {
      const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
      const png = [137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => header[index] === value);
      const jpeg = header[0] === 255 && header[1] === 216 && header[2] === 255;
      const webp = new TextDecoder().decode(header.slice(0, 4)) === 'RIFF' && new TextDecoder().decode(header.slice(8, 12)) === 'WEBP';
      if (!({ 'image/png': png, 'image/jpeg': jpeg, 'image/webp': webp })[file.type]) throw new Error('file_invalid');
      original = await dataUrlFrom(file);
      source = await imageFrom(original);
    }
    if (!source?.naturalWidth || source.naturalWidth * source.naturalHeight > 20000000) throw new Error('file_invalid');
    const scale = Math.min(1, 1600 / Math.max(source.naturalWidth, source.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(source.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(source.naturalHeight * scale));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    const dataUrl = ['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type) && original.length <= MAX_FILE ? original : canvas.toDataURL('image/png');
    if (dataUrl.length > MAX_FILE) throw new Error('file_invalid');
    const originalKept = dataUrl === original;
    const name = (originalKept ? file.name : `${file.name.replace(/\.[^.]+$/, '')}.png`).replace(/[\u0000-\u001f\u007f/\\]/g, '_') || 'company-logo.png';
    const artwork = { dataUrl, thumbnail: canvas.toDataURL('image/png'), name: name.slice(0, 140) };
    const sourcePixels = context.getImageData(0, 0, canvas.width, canvas.height);
    let converted, autoPrepared = false;
    try {
      const converter = globalThis.EngravingArtwork;
      converted = (converter.autoPrepare || converter.convert)(sourcePixels);
      autoPrepared = converted.analysis?.autoPrepared === true;
    } catch {
      // Keep the chosen original visible when neither preparation path works.
      // A lower-ranked supplier badge must never silently replace it.
      if (allowUnprepared) return { ...artwork, mask: null, needsPreparation: true };
      throw new Error('artwork_error');
    }
    const mask = document.createElement('canvas');
    mask.width = converted.bounds.width; mask.height = converted.bounds.height;
    const pixels = context.createImageData(converted.width, converted.height);
    pixels.data.set(converted.data);
    mask.getContext('2d').putImageData(pixels, -converted.bounds.x, -converted.bounds.y);
    return { ...artwork, mask, ...(autoPrepared ? { autoPrepared: true, preparationOptions: converted.options } : {}) };
  }

  class LogoCupMockup extends HTMLElement {
    q(selector) { return this.querySelector(selector); }
    t(key, values = {}) {
      const entities = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'", '#x27': "'" };
      return String(this.config?.strings?.[key] || key).replace(/&(amp|lt|gt|quot|apos|#39|#x27);/g, (_, entity) => entities[entity]).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => Object.hasOwn(values, name) ? String(values[name]) : match);
    }
    async connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.lifecycle = new AbortController();
      const lifecycle = this.lifecycle;
      if (this.orderBusy) {
        await this.orderSettled;
        if (!this.connected || lifecycle !== this.lifecycle || lifecycle.signal.aborted) return;
      }
      const confirmedOrder = this.orderConfirmed;
      this.logoVersion = (this.logoVersion || 0) + 1; this.photoVersion = (this.photoVersion || 0) + 1; this.catalogVersion = (this.catalogVersion || 0) + 1;
      this.finding = false; this.processing = false; this.continuing = false;
      this.vertical = this.vertical === true;
      this.products = []; this.productIndex = 0; this.photoReady = false;
      try {
        this.config = JSON.parse(this.q('[data-mockup-config]').textContent);
        this.studioReturnGuard=this.config.draftHandoff===true&&new URL(location.href).searchParams.has('personalization_draft');
        this.dataset.studioReturn=String(this.studioReturnGuard);this.studioRestoring=this.studioReturnGuard;this.studioProof=null;this.studioProofPending=false;this.handoffMessage='';
        for (let attempt = 0; attempt < 100 && !(globalThis.EngravingCupLibrary && globalThis.EngravingArtwork && globalThis.EngravingFileImport && globalThis.EngravingMockupEntry); attempt++) {
          if (!this.connected || lifecycle !== this.lifecycle || lifecycle.signal.aborted) return;
          await delay(50);
        }
        if (!this.connected || lifecycle !== this.lifecycle || lifecycle.signal.aborted) return;
        if (!(globalThis.EngravingCupLibrary && globalThis.EngravingArtwork && globalThis.EngravingFileImport && globalThis.EngravingMockupEntry)) throw new Error('dependencies_error');
        this.bind();
        this.q('[data-find]').disabled = !this.config.lookupEnabled;
        this.q('[data-upload]').disabled = false;
        if (!this.config.lookupEnabled) this.q('[data-logo-status]').textContent = this.t('lookup_disabled');
        await this.loadCups();
        if (!this.connected || lifecycle !== this.lifecycle || lifecycle.signal.aborted) return;
        if (confirmedOrder) this.orderSuccess({ recovered: true }, false);
        await this.initOrder();
        if(this.studioReturnGuard&&this.connected&&lifecycle===this.lifecycle&&!lifecycle.signal.aborted)await this.restoreStudioSource();
      } catch { if (lifecycle === this.lifecycle && !lifecycle.signal.aborted) { this.studioRestoring=false;this.error('dependencies_error'); this.stageStatus('cups_error', false);this.updateStudioEntry(); } }
    }
    disconnectedCallback() {
      this.connected = false;
      this.lifecycle?.abort(); this.lookup?.abort();
      this.studioOperation?.abort();
      this.logoVersion++; this.photoVersion++; this.catalogVersion++;
    }
    bind() {
      const on = (selector, event, handler) => this.q(selector).addEventListener(event, handler, { signal: this.lifecycle.signal });
      if(this.config.draftHandoff===true){
        on('[data-studio-review]','click',()=>this.continueDesigning('review'));
        on('[data-studio-simple]','click',()=>this.startSimpleDesign());
        for(const image of this.querySelectorAll('[data-studio-proof-side] img'))image.addEventListener('error',()=>{if(this.studioProof?.sides.some(side=>side.dataUrl===image.getAttribute('src'))){this.studioProof=null;this.renderStudioProof();}},{signal:this.lifecycle.signal});
        for(const type of ['input','change','click'])this.addEventListener(type,event=>{if(this.studioReturnGuard&&event.target.closest('.lcm__controls,.lcm__cup-controls,[data-quantity],[data-back-vertical],[data-add-cart]')){event.preventDefault();event.stopImmediatePropagation();}},{capture:true,signal:this.lifecycle.signal});
      }
      on('[data-website-form]', 'submit', event => { event.preventDefault(); if (!event.isComposing) this.findLogo(); });
      on('[data-website]', 'input', () => {
        if (this.orderBusy || this.sourceLocked()) return;
        this.resetOrderConfirmation();
        this.lookup?.abort(); this.logoVersion++;
        this.processing = false; this.setFinding(false); this.error(); this.q('[data-website]').removeAttribute('aria-invalid');
        this.q('[data-logo-status]').textContent = '';
      });
      on('[data-upload]', 'change', event => { const file=event.target.files?.[0];event.target.value='';this.upload(file); });
      on('[data-cup]', 'change', event => this.selectModel(Number(event.target.value)));
      on('[data-size]', 'change', event => this.selectProduct(Number(event.target.value)));
      on('[data-color]', 'change', event => this.selectVariant(event.target.value));
      on('[data-retry-cups]', 'click', () => this.loadCups());
      on('[data-back-vertical]', 'click', () => {
        if (this.q('[data-back-vertical]').disabled) return;
        this.pinnedPlacement=null;
        this.resetOrderConfirmation();
        this.vertical = !this.vertical; this.draw(); this.updateContinue();
      });
      on('[data-continue]', 'click', () => this.continueDesigning());
      if (this.q('[data-order]')) {
        on('[data-quantity]', 'input', () => {
          if (this.orderBusy || this.sourceLocked()) return;
          this.orderConfirmed = false;
          if (!this.orderBlocked) this.clearOrderFeedback();
          this.updateContinue();
        });
        on('[data-add-cart]', 'click', () => this.addToCart());
        on('[data-cart-recheck]', 'click', () => this.recoverCart(true));
      }
      // Back from the studio restores the editable page rather than a stuck CTA.
      window.addEventListener('pageshow', () => {
        this.continuing = false;
        if (!this.orderBusy && this.cartClient?.hasPending) this.recoverCart(false);
        if(this.studioReturnGuard)this.refreshStudioProof();
        this.updateContinue();
      }, { signal: this.lifecycle.signal });
      window.addEventListener('pagehide',()=>this.studioOperation?.abort(),{signal:this.lifecycle.signal});
    }
    sourceLocked(){return Boolean(this.studioBusy||this.studioReturnGuard&&!this.studioRestoring);}
    error(key = '') { const node = this.q('[data-error]'); node.textContent = key ? this.t(key) : ''; node.hidden = !key; }
    preparationStatus() { return this.logo?.source === 'site-icon' ? 'site_icon_needs_preparation' : 'logo_needs_preparation'; }
    stageStatus(key, busy) {
      this.q('[data-stage]').setAttribute('aria-busy', String(busy));
      const status = this.q('[data-preview-status]'); status.textContent = key ? this.t(key) : ''; status.hidden = !key;
    }
    setFinding(busy) {
      this.finding = busy;
      this.q('[data-find]').disabled = busy || !this.config.lookupEnabled;
      this.q('[data-find]').setAttribute('aria-busy', String(busy));
      if (busy) this.q('[data-logo-status]').textContent = this.t('find_busy');
      this.updateContinue();
    }
    async loadCups() {
      if (this.orderBusy || this.sourceLocked()) return;
      this.resetOrderConfirmation();
      const version = ++this.catalogVersion;
      const previousProductId = this.product?.id, previousVariantId = this.variant?.id;
      this.photoVersion++; this.photoReady = false; this.updateContinue();
      this.q('[data-cup-fields]').disabled = true;
      this.q('[data-retry-cups]').hidden = true;
      this.q('[data-catalog-status]').textContent = this.t('cups_loading');
      this.stageStatus('cups_loading', true);
      const result = await globalThis.EngravingCupLibrary.load({ enabled: true, products: [], rootUrl: this.config.rootUrl, signal: this.lifecycle.signal });
      if (!this.connected || version !== this.catalogVersion) return;
      // A physical cup can also belong to a flower, nurse or wedding listing.
      // Only merchant-selected logo listings may supply this page's cart identity.
      const allowed = new Map((this.config.productHandles || []).map((handle, index) => [handle, index]));
      const available = result.products.filter(product => allowed.has(product.handle)).sort((a, b) => allowed.get(a.handle) - allowed.get(b.handle)).map(product => ({ ...product, variants: product.variants.filter(variant => variant.available && backPreview(variant)) })).filter(product => product.variants.length);
      this.products = globalThis.EngravingCupLibrary.catalogProducts(available);
      if (!this.products.length) {
        const key = result.status === 'loaded' ? 'cups_empty' : 'cups_error';
        this.q('[data-catalog-status]').textContent = this.t(key);
        this.q('[data-retry-cups]').hidden = false;
        this.stageStatus(key, false); return;
      }
      this.q('[data-cup-fields]').disabled = false;
      this.q('[data-catalog-status]').textContent = result.status === 'partial' ? this.t('cups_partial') : '';
      this.q('[data-retry-cups]').hidden = result.status !== 'partial';
      const retainedIndex = this.products.findIndex(product => String(product.id) === String(previousProductId) && product.variants.some(variant => String(variant.id) === String(previousVariantId)));
      if (retainedIndex >= 0) { this.selectProduct(retainedIndex, previousVariantId); return; }
      const preferred = this.config.initialCup || DEFAULT_CUP;
      const normalize = value => String(value || '').trim().replace(/\s+/g, '').toLowerCase();
      const isDefault = variant => normalize(variant.color) === normalize(preferred.color);
      const defaultIndex = this.products.findIndex(product => product.handle === preferred.handle && (!preferred.size || normalize(product.cupSize) === normalize(preferred.size)) && product.variants.some(isDefault));
      this.selectProduct(defaultIndex < 0 ? 0 : defaultIndex, defaultIndex < 0 ? undefined : this.products[defaultIndex].variants.find(isDefault).id);
    }
    options(node, entries, selected) {
      node.replaceChildren(...entries.map(entry => {
        const option = document.createElement('option'); option.value = String(entry.value); option.textContent = entry.title; return option;
      }));
      node.value = String(selected);
    }
    modelKey(product) { return product?.cupModelKey || `listing:${product?.id}`; }
    isOwala(product = this.product) { return /\bowala\b/i.test(product?.cupModelTitle || product?.handle || (product === this.product ? this.variant?.physicalName : '') || ''); }
    selectModel(index) {
      const target = this.products[index];
      if (this.orderBusy || this.sourceLocked() || !target) return;
      const key = this.modelKey(target), remembered = this.modelSelections?.get(key);
      const members = this.products.map((product, index) => ({ product, index })).filter(item => this.modelKey(item.product) === key);
      const retained = remembered && members.find(({ product }) => String(product.id) === remembered.productId && product.variants.some(variant => String(variant.id) === remembered.variantId));
      if (retained) { this.selectProduct(retained.index, remembered.variantId); return; }
      if (this.isOwala(target)) {
        const dark = variant => variant.color?.trim().replace(/\s+/g, ' ').toLowerCase() === 'very very dark';
        const sizes = members.filter(({ product }) => /^32\s*oz$/i.test(product.cupSize || ''));
        const preferred = sizes.find(({ product }) => product.variants.some(dark)) || sizes[0];
        if (preferred) { this.selectProduct(preferred.index, (preferred.product.variants.find(dark) || preferred.product.variants[0]).id); return; }
      }
      this.selectProduct(index);
    }
    selectProduct(index, preferredVariantId) {
      if (this.orderBusy || this.sourceLocked() || !this.products[index]) return;
      this.pinnedPlacement=null;
      const previousColor = this.variant?.color;
      this.productIndex = index; this.product = this.products[index];
      const library = globalThis.EngravingCupLibrary;
      const options = { currentIndex: index };
      this.options(this.q('[data-cup]'), library.pickerModels(this.products, options).map(entry => ({ value: entry.index, title: entry.title })), index);
      const sizes = library.pickerSizes(this.products, options);
      this.options(this.q('[data-size]'), sizes.map(entry => ({ value: entry.index, title: entry.title })), index);
      this.q('[data-size-field]').hidden = sizes.length === 0;
      this.options(this.q('[data-color]'), this.product.variants.map(variant => ({ value: variant.id, title: variant.color })), this.product.variants[0].id);
      this.q('[data-swatches]').replaceChildren(...this.product.variants.map(item => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'lcm__swatch';
        button.dataset.variantId = String(item.id);
        button.setAttribute('aria-label', item.color); button.title = item.color;
        const chip = document.createElement('span'); chip.setAttribute('aria-hidden', 'true');
        if (/^#[a-f\d]{6}$/i.test(item.colorHex)) chip.style.setProperty('--swatch', item.colorHex);
        button.append(chip); button.addEventListener('click', () => this.selectVariant(item.id)); return button;
      }));
      this.selectVariant((this.product.variants.find(variant => String(variant.id) === String(preferredVariantId)) || this.product.variants.find(variant => variant.color === previousColor) || this.product.variants[0]).id);
    }
    selectVariant(id) {
      if (this.orderBusy || this.sourceLocked()) return;
      this.pinnedPlacement=null;
      const variant = this.product?.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      this.resetOrderConfirmation();
      this.variant = variant; this.q('[data-color]').value = String(variant.id);
      this.modelSelections ||= new Map();
      this.modelSelections.set(this.modelKey(this.product), { productId: String(this.product.id), variantId: String(variant.id) });
      [...this.q('[data-swatches]').children].forEach(button => button.setAttribute('aria-pressed', String(button.dataset.variantId === String(variant.id))));
      this.q('[data-cup-caption]').textContent = `${this.product.displayTitle || this.product.title} · ${variant.color} · ${this.t('back')}`;
      this.loadPhoto();
    }
    async loadPhoto() {
      const version = ++this.photoVersion;
      this.photoReady = false; this.photo = null; this.updateContinue();
      this.q('[data-preview]').hidden = true; this.q('[data-initial-photo]').hidden = true;
      this.stageStatus('photo_loading', true);
      try {
        const preview = backPreview(this.variant);
        if (!preview) throw new Error('photo_error');
        const photo = await imageFrom(preview.image);
        if (!this.connected || version !== this.photoVersion) return;
        this.photo = photo; this.photoReady = true;
        this.draw(); this.stageStatus('', false);
      } catch {
        if (!this.connected || version !== this.photoVersion) return;
        this.stageStatus('photo_error', false); this.q('[data-retry-cups]').hidden = false;
      }
      this.updateContinue();
    }
    async findLogo() {
      if (this.orderBusy || this.sourceLocked() || this.finding || !this.config.lookupEnabled) return;
      let url;
      try { url = websiteUrl(this.q('[data-website]').value); }
      catch { this.error('invalid_url'); this.q('[data-website]').setAttribute('aria-invalid', 'true'); this.q('[data-website]').focus(); return; }
      this.resetOrderConfirmation();
      this.lookup?.abort(); this.processing = false; this.lookup = new AbortController();
      const request = this.lookup, version = ++this.logoVersion;
      this.error(); this.q('[data-website]').removeAttribute('aria-invalid'); this.setFinding(true);
      const timer = setTimeout(() => request.abort(), 20000);
      try {
        const response = await fetch(ENDPOINT, { method: 'POST', credentials: 'omit', redirect: 'error', signal: request.signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ url }) });
        if (response.status === 429) throw new Error('rate_limit');
        if (!response.ok) throw new Error('lookup_error');
        if (!response.headers.get('content-type')?.includes('application/json') || Number(response.headers.get('content-length')) > 12 * MAX_FILE) throw new Error('lookup_error');
        const text = await response.text();
        if (text.length > 12 * MAX_FILE) throw new Error('lookup_error');
        const body = JSON.parse(text);
        if (body.ok !== true || !Array.isArray(body.data?.logos)) throw new Error('lookup_error');
        const candidates = [];
        let primaryUnavailable = false;
        if (!this.connected || version !== this.logoVersion) return;
        this.q('[data-logo-status]').textContent = this.t('artwork_busy');
        for (const [index, item] of body.data.logos.slice(0, 6).entries()) {
          if (!this.connected || version !== this.logoVersion) return;
          try {
            const suffix = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/svg+xml': 'svg' }[item.mimeType];
            const logo = await prepareWithin(() => prepareLogo(dataFile(item.dataUrl, item.mimeType, `website-logo.${suffix}`), true), request.signal);
            if (request.signal.aborted) throw new Error('lookup_error');
            candidates.push({ ...logo, source: item.source, label: typeof item.label === 'string' ? item.label.slice(0, 140) : '' });
          } catch (error) { if (request.signal.aborted) throw error; if (index === 0) primaryUnavailable = true; }
        }
        if (!this.connected || version !== this.logoVersion) return;
        this.showLogos(candidates, !primaryUnavailable);
        this.q('[data-logo-status]').textContent = this.t(primaryUnavailable || this.logo?.needsPreparation ? this.preparationStatus() : this.logo?.autoPrepared ? 'auto_prepared' : this.logo?.source === 'site-icon' ? 'site_icon' : candidates.length ? 'found' : 'not_found');
      } catch (error) {
        if (!this.connected || version !== this.logoVersion) return;
        this.error(error.message === 'rate_limit' ? 'rate_limit' : 'lookup_error');
        this.q('[data-logo-status]').textContent = '';
      } finally {
        clearTimeout(timer);
        if (this.connected && version === this.logoVersion) this.setFinding(false);
      }
    }
    async upload(file) {
      if (this.orderBusy || this.sourceLocked() || !file) return;
      this.q('[data-upload]').value='';
      this.resetOrderConfirmation();
      this.lookup?.abort(); const version = ++this.logoVersion;
      this.setFinding(false); this.processing = true; this.updateContinue(); this.error();
      this.q('[data-logo-status]').textContent = this.t('artwork_busy');
      try {
        const logo = await prepareWithin(() => prepareLogo(file), this.lifecycle.signal);
        if (!this.connected || version !== this.logoVersion) return;
        this.showLogos([logo]); this.q('[data-logo-status]').textContent = this.t(logo.autoPrepared ? 'auto_prepared' : 'logo_selected');
        const uploadFocused = document.activeElement === this.q('[data-upload]');
        this.q('[data-upload-disclosure]').open = false;
        if (uploadFocused) this.q('[data-upload-summary]').focus();
      } catch (error) {
        if (!this.connected || version !== this.logoVersion) return;
        this.error(['artwork_error', 'file_error'].includes(error.message) ? error.message : 'file_invalid');
        this.q('[data-logo-status]').textContent = '';
      } finally {
        if (this.connected && version === this.logoVersion) { this.processing = false; this.updateContinue(); this.q('[data-upload]').value = ''; }
      }
    }
    showLogos(logos, selectFirst = true) {
      if (this.orderBusy || this.sourceLocked()) return;
      this.pinnedPlacement=null;
      this.resetOrderConfirmation();
      this.logos = logos; this.logo = selectFirst ? logos[0] || null : null;
      this.syncUploadName();
      const list = this.q('[data-logo-list]');
      list.replaceChildren(...logos.map((logo, index) => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'lcm__logo';
        button.setAttribute('aria-label', `${this.t('logo_label')} ${index + 1}${logo.label ? `: ${logo.label}` : ''}`);
        button.setAttribute('aria-pressed', String(logo === this.logo));
        const image = document.createElement('img'); image.src = logo.thumbnail; image.alt = ''; button.append(image);
        button.addEventListener('click', () => {
          if (this.orderBusy || this.sourceLocked()) return;
          this.pinnedPlacement=null;
          this.resetOrderConfirmation();
          this.logo = logo; this.error();
          this.syncUploadName();
          [...list.children].forEach((child, position) => child.setAttribute('aria-pressed', String(position === index)));
          this.q('[data-logo-status]').textContent = this.t(logo.needsPreparation ? this.preparationStatus() : logo.autoPrepared ? 'auto_prepared' : logo.source === 'site-icon' ? 'site_icon' : 'logo_selected');
          this.draw(); this.updateContinue();
        }); return button;
      }));
      this.q('[data-logo-choices]').hidden = !logos.length;
      this.draw(); this.updateContinue();
    }
    syncUploadName() {
      const name = this.q('[data-upload-name]');
      name.textContent = this.logo && !this.logo.source ? this.logo.name || '' : '';
      name.hidden = !name.textContent;
    }
    logoSize() {
      const mask = this.logo?.mask;
      // Horizontal wordmarks should use the full available engraving width.
      return mask?.width > 0 && mask?.height > 0 && mask.width / mask.height >= 1.5 ? 100 : this.isOwala() ? 90 : 70;
    }
    logoPlacement() {
      if(this.pinnedPlacement)return {...this.pinnedPlacement};
      const area = backPreview(this.variant)?.area, mask = this.logo?.mask;
      if (!area || !mask) return { size: 70, x: 50, y: 50, rotation: 0 };
      const aspect = mask.width / mask.height, ratio = 1 / aspect;
      const owala = this.isOwala();
      // Fuller vertical artwork starts with breathing room. Only very skinny
      // wordmarks use the full rotated fit; Studio can enlarge either later.
      const fraction = this.vertical ? (aspect >= 4 ? 1 : owala ? .85 : .7) : this.logoSize() / 100;
      // Studio stores the unrotated width as a percentage of the engraving area.
      // A vertical wordmark can use the area's height, so its size may exceed 100.
      const width = this.vertical ? Math.min(area.height, area.width / ratio) * fraction : Math.min(area.width * fraction, area.height / ratio);
      const size = Math.min(10001, width / area.width * 100);
      const boundHeight = this.vertical ? area.width * size / 100 : area.width * size / 100 * ratio;
      const brrware = this.product?.handle === BRRWARE_HANDLE || /\bbrrware\b/i.test(this.variant.physicalName || this.product?.cupModelTitle || '');
      const desiredY = this.vertical ? 50 : owala ? 100 / 3 : brrware && aspect >= 1.5 ? 40 : 50;
      const halfHeight = boundHeight / area.height * 50;
      return { size, x: 50, y: Math.max(halfHeight, Math.min(100 - halfHeight, desiredY)), rotation: this.vertical ? -90 : 0 };
    }
    draw() {
      if (!this.photoReady || !this.photo || !this.variant) return;
      const preview = backPreview(this.variant);
      if (!preview) return;
      const canvas = this.q('[data-preview]'), ctx = canvas.getContext('2d');
      ctx.save(); ctx.scale(canvas.width / 1000, canvas.height / 1000);
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 1000, 1000);
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
      const image = this.photo, crop = preview.crop;
      const sw = image.naturalWidth * crop.width / 100, sh = image.naturalHeight * crop.height / 100;
      const scale = Math.min(1000 / sw, 1000 / sh), width = sw * scale, height = sh * scale;
      ctx.drawImage(image, image.naturalWidth * crop.x / 100, image.naturalHeight * crop.y / 100, sw, sh, (1000 - width) / 2, (1000 - height) / 2, width, height);
      if (this.logo?.mask) {
        const area = preview.area, mask = this.logo.mask;
        const placement = this.logoPlacement();
        const w = area.width * 10 * placement.size / 100, h = w * mask.height / mask.width;
        const finish = document.createElement('canvas'); finish.width = Math.max(1, Math.ceil(w * 2)); finish.height = Math.max(1, Math.ceil(h * 2));
        const silver = finish.getContext('2d'); silver.drawImage(mask, 0, 0, finish.width, finish.height);
        silver.globalCompositeOperation = 'source-in';
        const channels = (this.variant.colorHex || '#202020').slice(1).match(/../g).map(value => parseInt(value, 16) / 255).map(value => value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4);
        const light = channels[0] * .2126 + channels[1] * .7152 + channels[2] * .0722 >= .65;
        const gradient = silver.createLinearGradient(0, finish.height * .72, finish.width, finish.height * .22);
        (light ? [[0, '#697278'], [.39, '#a0a9ae'], [.49, '#b3bbc0'], [.79, '#778289'], [1, '#929da4']] : [[0, '#969fa5'], [.39, '#e0e5e8'], [.49, '#f3f6f7'], [.79, '#aeb7bd'], [1, '#d3dbe0']]).forEach(([stop, color]) => gradient.addColorStop(stop, color));
        silver.fillStyle = gradient; silver.fillRect(0, 0, finish.width, finish.height);
        ctx.save();
        ctx.translate((area.x + area.width * placement.x / 100) * 10, (area.y + area.height * placement.y / 100) * 10);
        ctx.rotate(placement.rotation * Math.PI / 180);
        ctx.drawImage(finish, -w / 2, -h / 2, w, h);
        ctx.restore();
      }
      ctx.restore(); canvas.hidden = false;
      let captionKey = 'preview_empty';
      if (this.logo?.needsPreparation) captionKey = this.preparationStatus();
      else if (this.logo?.mask) captionKey = this.logo.autoPrepared ? this.vertical ? 'preview_prepared_vertical' : 'preview_prepared' : this.vertical ? 'preview_vertical' : 'preview_ready';
      const caption = this.t(captionKey);
      canvas.setAttribute('aria-label', `${this.q('[data-cup-caption]').textContent}. ${caption}`);
      this.q('[data-preview-caption]').textContent = caption;
    }
    updateContinue() {
      const disabled = Boolean(!this.logo?.mask || !this.photoReady || !this.variant?.available || !backPreview(this.variant) || this.finding || this.processing || this.continuing || this.orderBusy || this.orderBlocked || (this.q('[data-order]') && !this.validQuantity()));
      const button = this.q('[data-continue]'); if (button) button.disabled = disabled;
      const vertical = this.q('[data-back-vertical]');
      if (vertical) { vertical.disabled = disabled; vertical.setAttribute('aria-pressed', String(this.vertical === true)); }
      this.updateOrder();
      this.updateStudioEntry();
    }
    async initOrder() {
      if (!this.q('[data-order]') || this.config.order?.checkoutEnabled !== true) return;
      const lifecycle = this.lifecycle;
      try {
        for (let attempt = 0; attempt < 100 && !(globalThis.EngravingMockupOrder && globalThis.EngravingCartSession && customElements.get('engraving-studio')); attempt++) {
          if (!this.connected || lifecycle.signal.aborted) return;
          await delay(50);
        }
        if (!this.connected || lifecycle.signal.aborted) return;
        if (!(globalThis.EngravingMockupOrder && globalThis.EngravingCartSession && customElements.get('engraving-studio'))) throw new Error('order_unavailable');
        this.cartClient ||= new globalThis.EngravingCartSession.Client(this.config.order);
        this.orderReady = true;
        if (this.cartClient.hasPending) await this.recoverCart(false);
        this.updateOrder();
      } catch (error) { this.orderFailure(error); }
    }
    clearOrderFeedback() {
      for (const selector of ['[data-order-status]', '[data-order-error]']) {
        const node = this.q(selector); if (node) { node.textContent = ''; if (selector === '[data-order-error]') node.hidden = true; }
      }
      for (const selector of ['[data-cart-open]', '[data-cart-recheck]']) { const node = this.q(selector); if (node) node.hidden = true; }
    }
    resetOrderConfirmation() {
      this.orderConfirmed = false;
      if (!this.orderBusy && !this.orderBlocked) this.clearOrderFeedback();
    }
    orderQuantity() { return Number(this.q('[data-quantity]')?.value); }
    minimumOrderQuantity() { return this.config?.bulk === true ? 10 : 1; }
    maximumOrderQuantity() { return this.config?.bulk === true ? 999 : 99; }
    validQuantity() { const quantity = this.orderQuantity(); return Number.isInteger(quantity) && quantity >= this.minimumOrderQuantity() && quantity <= this.maximumOrderQuantity(); }
    setOrderQuantity(quantity) {
      if (this.orderBusy || this.sourceLocked() || !Number.isInteger(quantity) || quantity < this.minimumOrderQuantity() || quantity > this.maximumOrderQuantity()) return false;
      const input = this.q('[data-quantity]');
      if (!input) return false;
      input.value = String(quantity);
      this.resetOrderConfirmation();
      this.updateContinue();
      return true;
    }
    orderState() {
      const quantity = this.orderQuantity(), validQuantity = this.validQuantity();
      const hasPrice = Number.isSafeInteger(this.variant?.price) && this.variant.price >= 0;
      const orderEnabled = Boolean(this.q('[data-order]') && this.config?.order?.checkoutEnabled === true);
      const busy = Boolean(this.orderBusy), blocked = Boolean(this.orderBlocked), confirmed = Boolean(this.orderConfirmed);
      const ready = Boolean(orderEnabled && this.orderReady && validQuantity && this.logo?.mask && this.photoReady && this.variant?.available && backPreview(this.variant) && hasPrice && !this.finding && !this.processing && !this.continuing && !busy && !blocked && !confirmed && !this.studioReturnGuard && !this.sourceLocked());
      return { product: this.product || null, variant: this.variant || null, quantity, validQuantity, busy, orderReady: Boolean(this.orderReady), orderEnabled, ready, confirmed, blocked };
    }
    orderGateAllows(state) {
      if (this.config?.bulk !== true) return true;
      if (globalThis.JaxBulkCheckout?.enabled === true && !globalThis.JaxBulkCart?.prepareForm) return false;
      if (state.quantity > 99 && globalThis.JaxBulkCheckout?.enabled !== true) return false;
      try { return this.bulkOrderGate?.(state) === true; }
      catch { return false; }
    }
    bulkDocument() {
      if (this.config?.bulk !== true || !this.logo?.dataUrl || !this.product || !this.variant) return null;
      return { kind: 'engraving-studio', version: 1, productId: String(this.product.id), variantId: String(this.variant.id), bulkSource: 'bulk-builder-v1',
        sides: { front: [], back: [{ id: 'bulk-logo', type: 'logo', assetId: 'bulk-logo', ...this.logoPlacement() }] },
        assets: [{ id: 'bulk-logo', mime: this.logo.dataUrl.slice(5, this.logo.dataUrl.indexOf(';')), original: this.logo.dataUrl }] };
    }
    updateOrder() {
      const state = this.orderState();
      state.allowed = this.orderGateAllows(state);
      if (!state.orderEnabled) {
        this.dispatchEvent(new CustomEvent('logo-mockup:change', { bubbles: true, detail: state }));
        return;
      }
      const quantity = state.quantity, valid = state.validQuantity;
      const price = this.variant?.price, hasPrice = Number.isSafeInteger(price) && price >= 0;
      let unit = '', total = '';
      try {
        if (hasPrice) {
          const formatter = new Intl.NumberFormat(this.config.order.locale || document.documentElement.lang || 'en', { style: 'currency', currency: this.config.order.currency });
          unit = this.t('unit_price', { price: formatter.format(price / 100) });
          if (valid) total = this.t('total_price', { price: formatter.format(price * quantity / 100) });
        }
      } catch { /* An unconfigured currency must not display an invented price. */ }
      const unitNode = this.q('[data-unit-price]'), totalNode = this.q('[data-total-price]');
      if (unitNode) unitNode.textContent = unit;
      if (totalNode) totalNode.textContent = total;
      this.q('[data-quantity]').disabled = Boolean(this.orderBusy || !this.orderReady);
      this.q('[data-quantity]').setAttribute('aria-invalid', String(!valid));
      if (!valid && !this.orderBusy && !this.orderBlocked) {
        const error = this.q('[data-order-error]'); error.textContent = this.t('quantity_invalid'); error.hidden = false;
      }
      state.ready = state.ready && Boolean(total);
      const add = this.q('[data-add-cart]');
      add.disabled = !state.ready || !state.allowed;
      add.setAttribute('aria-busy', String(Boolean(this.orderBusy)));
      add.textContent = this.t(this.orderBusy ? 'order_uploading' : this.orderConfirmed ? 'cart_added' : 'add_to_cart');
      this.q('[data-cart-recheck]').disabled = Boolean(this.orderBusy);
      this.dispatchEvent(new CustomEvent('logo-mockup:change', { bubbles: true, detail: state }));
    }
    lockOrderControls(busy) {
      if (busy) {
        this.orderSettled = new Promise(resolve => { this.settleOrder = resolve; });
        this.orderControlState = ['[data-website]', '[data-find]', '[data-upload]', '[data-cup-fields]', '[data-logo-choices]', '[data-retry-cups]'].map(selector => {
          const node = this.q(selector); const disabled = node?.disabled;
          if (node) node.disabled = true;
          return [node, disabled];
        });
      } else {
        for (const [node, disabled] of this.orderControlState || []) if (node) node.disabled = disabled;
        this.orderControlState = null;
      }
      this.orderBusy = busy;
      if (!busy) { this.settleOrder?.(); this.settleOrder = null; this.orderSettled = null; }
      this.updateContinue();
    }
    orderStage(stage) {
      const key = stage === 'upload' ? 'order_uploading' : stage === 'confirm' ? 'order_confirming' : 'order_preparing';
      this.q('[data-order-status]').textContent = this.t(key);
    }
    orderFailure(error) {
      const code = error?.code || error?.message;
      this.orderBlocked = Boolean(error?.ambiguous || this.cartClient?.hasPending);
      const own = code === 'mockup_order_changed' ? 'order_changed' : code === 'quantity_invalid' || code === 'order_unavailable' ? code : null;
      const known = ['cart_invalid', 'cart_rejected', 'cart_unconfirmed', 'cart_replace_unconfirmed', 'cart_storage_unavailable', 'unavailable', 'physical_unconfigured', 'back_preview_only', 'preview_only', 'export_error', 'image_error', 'back_image_failed'];
      const node = this.q('[data-order-error]');
      if (node) {
        node.textContent = own ? this.t(own) : known.includes(code) && this.config.order.strings?.[code] ? this.config.order.strings[code] : this.t('order_error');
        node.hidden = false; node.focus?.({ preventScroll: true });
      }
      const status = this.q('[data-order-status]'); if (status) status.textContent = '';
      const cart = this.q('[data-cart-open]'); if (cart) cart.hidden = false;
      const recheck = this.q('[data-cart-recheck]'); if (recheck) recheck.hidden = !this.orderBlocked;
      this.updateContinue();
    }
    orderSuccess(item, navigate = true) {
      this.orderBlocked = false; this.orderConfirmed = true;
      this.clearOrderFeedback();
      this.q('[data-order-status]').textContent = this.t(item?.recovered ? 'order_recovered' : 'order_success');
      this.q('[data-cart-open]').hidden = false;
      this.updateContinue();
      if (navigate && this.connected) {
        try { location.assign(this.cartClient.cartUrl); } catch { /* The confirmed item and View cart remain available. */ }
      }
    }
    async recoverCart(navigate = false) {
      if (this.orderBusy || !this.cartClient) return;
      this.clearOrderFeedback(); this.lockOrderControls(true); this.orderStage('confirm');
      try {
        const item = await this.cartClient.recover(stage => this.orderStage(stage));
        if (item) this.orderSuccess(item, navigate);
        else { this.orderBlocked = false; this.clearOrderFeedback(); }
      } catch (error) { this.orderFailure(error); }
      finally { this.lockOrderControls(false); }
    }
    async addToCart() {
      if (this.orderBusy || this.studioReturnGuard || this.sourceLocked() || this.orderBlocked || this.orderConfirmed || this.q('[data-add-cart]')?.disabled) return;
      if (!this.orderGateAllows(this.orderState())) { this.updateOrder(); return; }
      // A prior uncertain attempt belongs to its original design. Resolve it on
      // its own; Client.submit must never mistake it for this newly selected logo.
      if (this.cartClient?.hasPending) { await this.recoverCart(false); return; }
      if (!this.validQuantity()) return;
      const quantity = this.orderQuantity(), productId = String(this.product.id), variantId = String(this.variant.id);
      const logo = { dataUrl: this.logo.dataUrl, name: this.logo.name, mask: this.logo.mask, ...(this.logo.autoPrepared ? { preparationOptions: { ...this.logo.preparationOptions } } : {}) };
      const placement = this.logoPlacement(), lifecycle = this.lifecycle;
      this.clearOrderFeedback(); this.lockOrderControls(true); this.orderStage('prepare');
      try {
        const prepared = await globalThis.EngravingMockupOrder.prepare({ config: this.config.order, products: this.products, productId, variantId, logo, placement, quantity, ...(this.config.bulk === true ? { bulkSource: 'bulk-builder-v1' } : {}) });
        const form = await prepared.renderer.cartForm(prepared.document, quantity, prepared.document.sides);
        // Leaving during file generation is cancellation. Once Client starts the
        // write its journal owns completion/recovery, with no automatic replay.
        if (!this.connected || lifecycle.signal.aborted) return;
        if (this.cartClient.hasPending) { this.orderBlocked = true; throw Object.assign(new Error('cart_unconfirmed'), { ambiguous: true }); }
        if (!this.orderGateAllows(this.orderState())) { this.clearOrderFeedback(); return; }
        const cartSelection = this.config.bulk === true && globalThis.JaxBulkCheckout?.enabled === true ? await globalThis.JaxBulkCart.prepareForm(form, prepared.document, quantity, { source: null }) : { variantId };
        if (!this.connected || lifecycle.signal.aborted || !this.orderGateAllows(this.orderState())) return;
        const item = await this.cartClient.submit(form, { variantId: cartSelection.variantId, quantity, source: null, ...(this.config.bulk === true ? { document: prepared.document } : {}) }, stage => this.orderStage(stage));
        this.orderSuccess(item, !lifecycle.signal.aborted);
      } catch (error) { this.orderFailure(error); }
      finally { this.lockOrderControls(false); }
    }
    handoffText(key){return this.config.handoffStrings?.[key]||key;}
    sharedDraftRoute(){return this.config.draftHandoff===true&&this.config.bulk!==true&&!this.logo?.autoPrepared&&!this.logo?.needsPreparation;}
    studioTarget(product){
      const url=new URL(product.url,location.origin);if(url.origin!==location.origin||!/^\/(?:[a-zA-Z0-9_-]+\/)*products\/[a-z0-9-]+$/.test(url.pathname))throw new Error('unavailable');
      url.search='';url.hash='';const theme=new URL(location.href).searchParams.get('preview_theme_id');if(theme&&/^\d+$/.test(theme))url.searchParams.set('preview_theme_id',theme);return url.href;
    }
    handoffReason(){
      if(this.studioBusy||this.studioRestoring||this.orderBusy||this.finding||this.processing)return 'preparing';
      if(this.orderBlocked||this.orderConfirmed||this.cartClient?.hasPending)return 'cart_pending';
      if(this.product?.manualSizingReview||this.variant?.manualSizingReview)return 'manual_review';
      if(this.studioReturnGuard)return this.studioReturn?'':'restore_failed';
      if(!this.sharedDraftRoute()||!this.orderReady||this.config.order?.checkoutEnabled!==true||!this.orderState().ready)return 'unavailable';
      return '';
    }
    updateStudioEntry(){
      if(this.config?.draftHandoff!==true)return;
      const guarded=Boolean(this.studioReturnGuard),locked=guarded||this.studioBusy||this.studioRestoring;
      this.dataset.studioReturn=String(guarded);
      for(const selector of ['.lcm__controls','.lcm__cup-controls','[data-order]','[data-stage]']){const node=this.q(selector);if(node)node.hidden=guarded||selector==='[data-order]'&&this.config.order?.checkoutEnabled!==true;}
      for(const selector of ['[data-website]','[data-upload]','[data-find]','[data-cup-fields]','[data-logo-choices]','[data-quantity]']){
        const node=this.q(selector);if(!node)continue;
        if(locked){if(!('draftWasDisabled' in node.dataset))node.dataset.draftWasDisabled=String(node.disabled);node.disabled=true;}
        else if('draftWasDisabled' in node.dataset){node.disabled=node.dataset.draftWasDisabled==='true';delete node.dataset.draftWasDisabled;}
      }
      if(!locked){this.q('[data-cup-fields]').disabled=!this.products?.length||this.orderBusy;this.q('[data-quantity]').disabled=!this.orderReady||this.orderBusy;this.q('[data-find]').disabled=this.finding||!this.config.lookupEnabled||this.orderBusy;this.q('[data-upload]').disabled=Boolean(this.orderBusy);}
      const reason=this.handoffReason(),review=this.q('[data-studio-review]'),separate=this.q('[data-studio-simple]');
      review.hidden=!guarded;review.disabled=Boolean(reason);separate.hidden=!guarded;separate.disabled=Boolean(this.studioBusy||this.studioRestoring||this.orderBusy||this.orderBlocked||this.orderConfirmed||this.cartClient?.hasPending||!this.orderReady);
      const button=this.q('[data-continue]'),status=this.q('[data-handoff-status]');
      if(guarded||this.sharedDraftRoute()){button.disabled=Boolean(reason);button.textContent=this.handoffText(this.studioBusy?'preparing':guarded?'resume':'action');}
      const message=this.handoffMessage||(this.studioBusy?'preparing':guarded?(this.studioReturn?(this.studioProof?'advanced_help':'proof_unavailable'):'restore_failed'):reason&&this.logo?.mask?reason:'live_help');
      status.textContent=this.handoffText(message);status.hidden=guarded||!this.sharedDraftRoute()&&!this.handoffMessage;
      this.renderStudioProof();
    }
    renderStudioProof(){
      const panel=this.q('[data-studio-return-preview]');if(!panel)return;
      panel.hidden=!this.studioReturnGuard;const record=this.studioReturn?.record,doc=record?.document;
      panel.querySelector('[data-studio-return-summary]').textContent=record?[doc.productTitle,doc.variantTitle,this.handoffText('advanced_quantity').replace('__COUNT__',String(record.quantity))].filter(Boolean).join(' · '):'';
      for(const figure of panel.querySelectorAll('[data-studio-proof-side]')){const proof=record&&this.studioProof?.sides.find(side=>side.side===figure.dataset.studioProofSide),image=figure.querySelector('img');figure.hidden=!proof;if(proof){image.src=proof.dataUrl;image.width=proof.width;image.height=proof.height;}else image.removeAttribute('src');}
      const reason=this.handoffReason();
      const issue=['failed','storage','unavailable','restore_failed','cart_pending'].includes(this.handoffMessage)?this.handoffMessage:['cart_pending','manual_review'].includes(reason)?reason:null;
      const pending=this.studioRestoring||this.studioProofPending;
      panel.setAttribute('aria-busy',String(!!pending));
      panel.querySelector('[data-studio-proof-status]').textContent=this.handoffText(pending?'preparing':issue||(!record?'restore_failed':this.studioProof?'advanced_help':'proof_unavailable'));
    }
    async refreshStudioProof(record){
      const returning=this.studioReturn,lifecycle=this.lifecycle,request=this.studioProofRequest=(this.studioProofRequest||0)+1;
      const current=()=>request===this.studioProofRequest&&this.studioReturn===returning&&this.studioReturnGuard&&this.connected&&lifecycle===this.lifecycle&&!lifecycle.signal.aborted;
      this.studioProof=null;this.studioProofPending=!!returning;if(!this.config?.draftHandoff)return;this.renderStudioProof();if(!returning)return;
      try{
        if(!record){const saved=JEDesignHandoff.inspect({route:JEDesignHandoff.destination(returning.targetUrl,returning.token)});if(saved.status!=='ready')throw new Error();record=saved.record;}
        const proof=await JEDesignHandoff.proofFor(record);
        if(!current())return;
        returning.record=record;this.studioProof=proof;
      }catch{if(!current())return;this.studioReturn=null;this.handoffMessage='restore_failed';}
      this.studioProofPending=false;this.updateStudioEntry();
    }
    async openStudioDraft(step){
      if(this.handoffReason())return;
      const transport=globalThis.JEDesignHandoff;if(!transport){this.handoffMessage='failed';this.updateStudioEntry();return;}
      const lifecycle=this.lifecycle,operation=new AbortController(),version=[this.logoVersion,this.catalogVersion,this.photoVersion].join(':');
      const current=()=>this.connected&&this.lifecycle===lifecycle&&!lifecycle.signal.aborted&&!operation.signal.aborted&&version===[this.logoVersion,this.catalogVersion,this.photoVersion].join(':');
      this.studioOperation=operation;this.studioBusy=true;this.continuing=true;this.handoffMessage='';this.updateContinue();let timer;
      try{
        if(this.studioReturn){
          const saved=transport.inspect({route:transport.destination(this.studioReturn.targetUrl,this.studioReturn.token)});
          if(saved.status!=='ready'){this.studioReturn=null;this.studioProof=null;throw new Error('restore_failed');}
          const product=this.products.find(item=>String(item.id)===saved.record.document.productId&&item.variants.some(variant=>String(variant.id)===saved.record.document.variantId)),variant=product?.variants.find(item=>String(item.id)===saved.record.document.variantId);
          if(!variant?.available||product.manualSizingReview||variant.manualSizingReview)throw new Error('unavailable');
          if(current())location.assign(transport.destination(this.studioTarget(product),saved.token,{variantId:String(variant.id),...(step==='review'?{step}:{})}));return;
        }
        const productId=String(this.product.id),variantId=String(this.variant.id),quantity=this.orderQuantity(),vertical=this.vertical===true;
        const logo={dataUrl:this.logo.dataUrl,name:this.logo.name,mask:this.logo.mask},placement=this.logoPlacement();
        const interrupted=new Promise((_,reject)=>{operation.signal.addEventListener('abort',()=>reject(new Error('cancelled')),{once:true});timer=setTimeout(()=>{operation.timedOut=true;operation.abort();},45000);});
        const prepared=await Promise.race([EngravingMockupOrder.prepare({config:this.config.order,products:this.products,productId,variantId,logo,placement,quantity}),interrupted]);
        if(!current())return;if(this.cartClient?.hasPending||this.orderBlocked||this.orderConfirmed)throw new Error('cart_pending');
        const document=JSON.parse(JSON.stringify(prepared.document));if(document.productId!==productId||document.variantId!==variantId||prepared.quantity!==quantity)throw new Error('unavailable');
        const token=crypto.randomUUID(),sourceUrl=new URL(location.href);sourceUrl.searchParams.set('personalization_draft',token);
        const record={version:1,source:{url:sourceUrl.pathname+sourceUrl.search+sourceUrl.hash,editor:'logo-mockup'},document,sourceDocument:document,quantity,
          sourceSnapshot:{version:1,kind:'logo-mockup',productId,variantId,quantity,vertical}};
        const targetUrl=this.studioTarget(this.product),destination=transport.destination(targetUrl,token,{variantId});
        transport.save(record,{token});if(!current())return;
        this.studioReturn={token,targetUrl,record};this.studioReturnGuard=true;
        history.replaceState(history.state,'',sourceUrl.href);location.assign(destination);
      }catch(error){if(current()||operation.timedOut&&this.connected&&this.lifecycle===lifecycle)this.handoffMessage=['restore_failed','unavailable','cart_pending'].includes(error.message)?error.message:error.message==='design_handoff_storage'?'storage':'failed';}
      finally{clearTimeout(timer);if(this.studioOperation===operation){this.studioOperation=null;this.studioBusy=false;this.continuing=false;if(this.connected)this.updateContinue();}}
    }
    async restoreStudioSource(){
      const lifecycle=this.lifecycle;this.studioRestoring=true;this.updateStudioEntry();
      try{
        if(!this.orderReady||this.orderBlocked||this.orderConfirmed||this.cartClient?.hasPending)throw new Error();
        const route=new URL(location.href),token=route.searchParams.get('personalization_draft');if(route.searchParams.getAll('personalization_draft').length!==1)throw new Error();
        route.searchParams.delete('personalization_draft');route.searchParams.set('studio_draft',token);
        const saved=JEDesignHandoff.inspect({route:route.href}),snapshot=saved.record?.sourceSnapshot,original=saved.record?.sourceDocument;
        if(saved.status!=='ready'||saved.record.source.editor!=='logo-mockup'||snapshot?.kind!=='logo-mockup'||original?.productId!==snapshot.productId||original?.variantId!==snapshot.variantId||!Number.isInteger(snapshot.quantity)||snapshot.quantity<1||snapshot.quantity>99||typeof snapshot.vertical!=='boolean'||original.sides.front.length||original.sides.back.length!==1||original.assets.length!==1)throw new Error();
        const index=this.products.findIndex(item=>String(item.id)===snapshot.productId&&!item.manualSizingReview&&item.variants.some(variant=>String(variant.id)===snapshot.variantId&&variant.available&&!variant.manualSizingReview));if(index<0)throw new Error();
        const layer=original.sides.back[0],asset=original.assets[0];if(layer.type!=='logo'||layer.assetId!==asset.id||layer.preparationRequested||layer.personalization)throw new Error();
        const logo=await prepareLogo(dataFile(asset.original,asset.mime,asset.name));if(logo.autoPrepared||logo.needsPreparation||logo.dataUrl!==asset.original)throw new Error();
        const placement=Object.fromEntries(['size','x','y','rotation'].map(key=>[key,layer[key]]));
        const prepared=await EngravingMockupOrder.prepare({config:this.config.order,products:this.products,productId:snapshot.productId,variantId:snapshot.variantId,logo,placement,quantity:snapshot.quantity});
        if(prepared.renderer.importNeedsReview(original)||JSON.stringify(prepared.document.assets[0].options)!==JSON.stringify(asset.options))throw new Error();
        if(!this.connected||this.lifecycle!==lifecycle||lifecycle.signal.aborted)return;
        this.selectProduct(index,snapshot.variantId);this.showLogos([logo]);this.vertical=snapshot.vertical;this.pinnedPlacement=placement;this.q('[data-quantity]').value=String(snapshot.quantity);
        this.studioReturn={token,targetUrl:this.studioTarget(this.product),record:saved.record};this.handoffMessage='';await this.refreshStudioProof(saved.record);
      }catch{if(this.lifecycle===lifecycle&&!lifecycle.signal.aborted){this.studioReturn=null;this.studioProof=null;this.handoffMessage='restore_failed';}}
      finally{if(this.lifecycle===lifecycle){this.studioRestoring=false;if(this.connected)this.updateContinue();}}
    }
    startSimpleDesign(){
      if(!this.studioReturnGuard||this.studioBusy||this.studioRestoring||this.orderBusy||this.orderBlocked||this.orderConfirmed||this.cartClient?.hasPending||!this.orderReady)return;
      this.studioReturnGuard=false;this.studioReturn=null;this.studioProof=null;this.studioProofPending=false;this.studioProofRequest=(this.studioProofRequest||0)+1;this.handoffMessage='simple_started';
      const route=new URL(location.href);route.searchParams.delete('personalization_draft');history.replaceState(history.state,'',route.href);
      this.updateContinue();this.draw();
      const editor=this.q(this.config.lookupEnabled?'[data-website]':'[data-upload-summary]');
      if(editor&&!editor.matches(':disabled,[aria-disabled="true"]'))editor.focus();
    }
    continueDesigning(step) {
      if(this.config.draftHandoff===true&&(this.studioReturnGuard||this.config.bulk!==true&&!this.logo?.autoPrepared&&!this.logo?.needsPreparation))return this.openStudioDraft(step);
      if (!this.logo?.mask || this.q('[data-continue]').disabled) return;
      if (this.q('[data-order]') && !this.validQuantity()) { this.updateContinue(); return; }
      try {
        const url = new URL(this.product.url, location.origin);
        if (url.origin !== location.origin || !/^\/(?:[a-zA-Z0-9_-]+\/)*products\/[a-z0-9-]+$/.test(url.pathname)) throw new Error('invalid');
        const token = globalThis.EngravingMockupEntry.save({ productId: String(this.product.id), variantId: String(this.variant.id), dataUrl: this.logo.dataUrl, name: this.logo.name, side: 'back', ...this.logoPlacement(), ...(this.q('[data-order]') ? { quantity: this.orderQuantity(), ...(this.config.bulk === true ? { bulkOrder: true } : {}) } : {}), ...(this.logo.autoPrepared ? { preparationOptions: this.logo.preparationOptions } : {}) });
        url.search = ''; url.hash = '';
        url.searchParams.set('view', 'studio-customize'); url.searchParams.set('variant', this.variant.id); url.searchParams.set('studio_mockup', token);
        const theme = new URLSearchParams(location.search).get('preview_theme_id');
        if (theme && /^\d+$/.test(theme)) url.searchParams.set('preview_theme_id', theme);
        this.continuing = true; this.updateContinue(); this.error();
        this.q('[data-logo-status]').textContent = this.t('continue_busy');
        location.assign(url.href);
      } catch { this.error('storage_error'); this.continuing = false; this.updateContinue(); }
    }
  }
  customElements.define('logo-cup-mockup', LogoCupMockup);
})();
